import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { filter } from 'rxjs/operators';
import { FINDER_GROUPS, FinderGroup, FinderItem, PageFinderService } from './page-finder.service';

/** Before anything is typed the page's own controls are cut short, so the places to go show too. */
const PAGE_ITEMS_UNFILTERED = 10;
const MAX_SHOWN = 80;
const GROUP_ICON: Record<FinderGroup, string> = {
  'On this page': 'touch_app', 'This client': 'person', 'Sections': 'segment', 'Go to': 'arrow_forward',
  'Create': 'add', 'Apps': 'apps', 'Settings': 'settings',
};

interface Row { item: FinderItem; index: number; heading: FinderGroup | null; }

/**
 * The "What are you looking for?" card, opened from the top bar's finder button or Ctrl+/ (Ctrl+K is
 * the global search). It offers the page's own controls — picking one scrolls to it and makes it
 * pulse, so people learn where it lives — and the places to go: the record's sections, the app's
 * pages, Create, other apps and settings, which open directly.
 *
 * Mounted once in AppComponent. Its document listeners run outside Angular, so a click anywhere in
 * the app does not cost a change-detection pass on its account; the shortcut is the only one
 * attached while the card is closed.
 */
@Component({
  selector: 'bc-page-finder',
  standalone: true,
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // None: the pulse lands on controls of other components. Every class is bc-pf- prefixed.
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (finder.isOpen()) {
      <section class="bc-pf" role="dialog" aria-modal="false" aria-labelledby="bc-pf-title">
        <header class="bc-pf-head">
          <div>
            <h2 id="bc-pf-title" class="bc-pf-title">What are you looking for?</h2>
            <p class="bc-pf-sub">{{ inDialog() ? 'Actions in this window' : 'Actions on this page and places to go' }}</p>
          </div>
          <button type="button" class="bc-pf-icon-btn" aria-label="Close" (click)="finder.close()">
            <mat-icon aria-hidden="true">close</mat-icon>
          </button>
        </header>

        <div class="bc-pf-search">
          <mat-icon aria-hidden="true">search</mat-icon>
          <input #query type="text" autocomplete="off" placeholder="Type what you want to do"
                 aria-label="Filter actions" role="combobox" aria-controls="bc-pf-list" aria-expanded="true"
                 [attr.aria-activedescendant]="shown().length ? 'bc-pf-opt-' + active() : null"
                 [value]="term()" (input)="onInput($event)" (keydown)="onKeydown($event)">
        </div>

        @if (shown().length) {
          <ul id="bc-pf-list" class="bc-pf-list" role="listbox" aria-label="Actions and places">
            @for (row of shown(); track row.index) {
              @if (row.heading) { <li class="bc-pf-group" role="presentation">{{ row.heading }}</li> }
              <li [id]="'bc-pf-opt-' + row.index" role="option" class="bc-pf-item"
                  [class.bc-pf-active]="row.index === active()" [class.bc-pf-disabled]="!!row.item.disabledReason"
                  [attr.aria-selected]="row.index === active()" [attr.aria-disabled]="row.item.disabledReason ? 'true' : null"
                  [attr.title]="row.item.disabledReason || null"
                  (mouseenter)="active.set(row.index)" (click)="pick(row.item)">
                <mat-icon aria-hidden="true">{{ row.item.disabledReason ? 'lock' : (row.item.icon || groupIcon[row.item.group]) }}</mat-icon>
                <span class="bc-pf-label">{{ row.item.label }}</span>
                <!-- Typed: one ranked list, so each row says where it comes from. -->
                @if (term() ? row.item.hint || row.item.group : row.item.hint; as hint) {
                  <span class="bc-pf-kind">{{ hint }}</span>
                }
              </li>
            }
          </ul>
        } @else {
          <p class="bc-pf-empty" role="status">
            {{ term() ? 'Nothing matches “' + term() + '”.' : 'Nothing found on this page.' }}
          </p>
        }

        <footer class="bc-pf-foot">
          <button type="button" class="bc-pf-link" (click)="searchEverywhere()">
            <mat-icon aria-hidden="true">travel_explore</mat-icon> Not here? Search everything
          </button>
          <span class="bc-pf-keys" aria-hidden="true"><kbd>↑</kbd><kbd>↓</kbd> <kbd>Enter</kbd></span>
        </footer>
      </section>
    }
  `,
  styles: [`
    .bc-pf {
      position: fixed; top: calc(var(--bc-topbar-h, 64px) + 8px); right: var(--bc-space-6, 24px);
      /* Above Material dialogs (1000): when one is open the finder lists that dialog's actions. */
      z-index: 1100;
      width: 380px; max-width: calc(100vw - 32px); max-height: min(70vh, 560px);
      display: flex; flex-direction: column;
      background: var(--bc-surface); color: var(--bc-ink);
      border: 1px solid var(--bc-border); border-radius: var(--bc-radius-lg);
      box-shadow: var(--bc-shadow-pop); font: var(--bc-text-table);
    }
    .bc-pf-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--bc-space-2);
                  padding: var(--bc-space-4) var(--bc-space-4) var(--bc-space-2); }
    .bc-pf-title { margin: 0; font: var(--bc-text-label); }
    .bc-pf-sub { margin: 2px 0 0; font: var(--bc-text-meta); color: var(--bc-text-2); }
    .bc-pf-icon-btn { display: inline-flex; border: 0; background: transparent; color: var(--bc-text-2);
                      padding: 4px; border-radius: var(--bc-radius-sm); cursor: pointer; }
    .bc-pf-icon-btn:hover { background: var(--bc-row-hover); }
    .bc-pf-search { display: flex; align-items: center; gap: var(--bc-space-2); margin: 0 var(--bc-space-4) var(--bc-space-2);
                    padding: 0 var(--bc-space-3); height: 40px; border: 1px solid var(--bc-border-strong);
                    border-radius: var(--bc-radius-md); }
    .bc-pf-search:focus-within { border-color: var(--bc-brand); box-shadow: 0 0 0 1px var(--bc-brand); }
    .bc-pf-search mat-icon { color: var(--bc-text-3); font-size: 20px; width: 20px; height: 20px; }
    .bc-pf-search input { flex: 1; min-width: 0; border: 0; outline: none !important; background: transparent;
                          font: var(--bc-text-table); color: var(--bc-ink); }
    .bc-pf-list { list-style: none; margin: 0; padding: 0 var(--bc-space-2) var(--bc-space-2); overflow-y: auto; }
    .bc-pf-item { display: flex; align-items: center; gap: var(--bc-space-3); padding: var(--bc-space-2) var(--bc-space-3);
                  border-radius: var(--bc-radius-md); cursor: pointer; }
    .bc-pf-item mat-icon { color: var(--bc-text-3); font-size: 18px; width: 18px; height: 18px; flex-shrink: 0; }
    .bc-pf-item.bc-pf-active { background: var(--bc-brand-soft); color: var(--bc-brand-ink); }
    .bc-pf-item.bc-pf-active mat-icon { color: var(--bc-brand-ink); }
    .bc-pf-group { padding: var(--bc-space-3) var(--bc-space-3) var(--bc-space-1); font: var(--bc-text-group);
                   text-transform: uppercase; letter-spacing: .05em; color: var(--bc-text-2); }
    .bc-pf-item.bc-pf-disabled { cursor: not-allowed; color: var(--bc-text-3); }
    .bc-pf-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .bc-pf-kind { font: var(--bc-text-meta); color: var(--bc-text-2); }
    .bc-pf-empty { margin: 0; padding: var(--bc-space-4); font: var(--bc-text-meta); color: var(--bc-text-2); }
    .bc-pf-foot { display: flex; align-items: center; justify-content: space-between; gap: var(--bc-space-2);
                  padding: var(--bc-space-2) var(--bc-space-4); border-top: 1px solid var(--bc-border);
                  background: var(--bc-surface-subtle); border-radius: 0 0 var(--bc-radius-lg) var(--bc-radius-lg); }
    .bc-pf-link { display: inline-flex; align-items: center; gap: 6px; border: 0; background: transparent; padding: 4px 0;
                  font: var(--bc-text-meta); font-weight: 600; color: var(--bc-brand-ink); cursor: pointer; }
    .bc-pf-link mat-icon { font-size: 18px; width: 18px; height: 18px; }
    .bc-pf-keys { display: inline-flex; gap: 4px; }
    .bc-pf-keys kbd { font: 500 11px var(--bc-font-mono); color: var(--bc-text-2); border: 1px solid var(--bc-border-strong);
                      border-bottom-width: 2px; border-radius: var(--bc-radius-sm); padding: 0 5px; background: var(--bc-surface); }

    /* The control the user picked. Outline rather than border so nothing around it moves. */
    .bc-pf-pulse { outline: 3px solid var(--bc-brand) !important; outline-offset: 3px !important;
                   animation: bc-pf-pulse 0.8s ease-in-out 3; }
    @keyframes bc-pf-pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(10, 122, 82, 0.45); }
      50% { box-shadow: 0 0 0 8px rgba(10, 122, 82, 0); }
    }

    @media (max-width: 600px) {
      .bc-pf { right: 16px; left: 16px; width: auto; max-width: none; }
    }
    @media print { .bc-pf { display: none; } }
  `],
})
export class PageFinderComponent {
  protected readonly finder = inject(PageFinderService);
  private readonly zone = inject(NgZone);
  private readonly host = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;
  private readonly destroyRef = inject(DestroyRef);

  private readonly queryInput = viewChild<ElementRef<HTMLInputElement>>('query');

  protected readonly groupIcon = GROUP_ICON;

  protected readonly term = signal('');
  protected readonly active = signal(0);
  protected readonly inDialog = signal(false);
  private readonly items = signal<FinderItem[]>([]);

  /**
   * Nothing typed: everything under group headings (collect() already orders the groups), with the
   * page's own controls cut short. Typed: one list, best match first, whatever group it is in —
   * "finance" should put the Finance app or section on top, not a button that mentions it.
   */
  protected readonly shown = computed<Row[]>(() => {
    const term = this.term().trim().toLowerCase();
    const words = term.split(/\s+/).filter(Boolean);
    const all = this.items();

    if (!words.length) {
      let onPage = 0;
      const picked = all.filter(item => item.group !== 'On this page' || onPage++ < PAGE_ITEMS_UNFILTERED);
      return picked.slice(0, MAX_SHOWN).map((item, index) => ({
        item, index, heading: index === 0 || picked[index - 1].group !== item.group ? item.group : null,
      }));
    }

    return all
      .map((item, order) => ({ item, order, score: matchScore(item, term, words) }))
      .filter(x => x.score !== null)
      .sort((a, b) => a.score! - b.score! || a.order - b.order)
      .slice(0, MAX_SHOWN)
      .map((x, index) => ({ item: x.item, index, heading: null }));
  });

  /** Removes the listeners that exist only while the card is open. */
  private detachOpenListeners: (() => void) | undefined;

  constructor() {
    this.zone.runOutsideAngular(() => {
      const onKey = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key === '/') {
          e.preventDefault();
          this.zone.run(() => this.finder.toggle());
        }
      };
      document.addEventListener('keydown', onKey);
      this.destroyRef.onDestroy(() => document.removeEventListener('keydown', onKey));
    });

    // Opening re-reads the page, so the list always matches what is on screen now.
    effect(() => {
      if (this.finder.isOpen()) this.onOpened();
      else this.onClosed();
    });

    // A new page has other actions; the card belongs to the one it was opened on.
    inject(Router).events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.finder.close());

    this.destroyRef.onDestroy(() => this.detachOpenListeners?.());
  }

  protected onInput(event: Event): void {
    this.term.set((event.target as HTMLInputElement).value);
    this.active.set(0);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const count = this.shown().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count) this.active.update(i => (i + 1) % count);
        this.scrollActiveIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count) this.active.update(i => (i - 1 + count) % count);
        this.scrollActiveIntoView();
        break;
      case 'Escape':
        // Stopped here so an open dialog underneath does not close with the finder.
        event.preventDefault();
        event.stopPropagation();
        this.finder.close();
        break;
      case 'Enter': {
        event.preventDefault();
        const row = this.shown()[this.active()];
        if (row) this.pick(row.item);
        break;
      }
    }
  }

  protected pick(item: FinderItem): void {
    if (item.disabledReason) return;
    this.finder.close();
    // After the card is gone, so a revealed control keeps the focus it is given.
    setTimeout(() => item.run());
  }

  protected searchEverywhere(): void {
    this.finder.close();
    setTimeout(() => {
      const search = document.getElementById('bc-global-search') as HTMLInputElement | null;
      search?.focus();
      if (search && this.term()) {
        search.value = this.term();
        search.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
  }

  private onOpened(): void {
    const { items, inDialog } = this.finder.collect();
    this.items.set(items);
    this.inDialog.set(inDialog);
    this.term.set('');
    this.active.set(0);
    setTimeout(() => this.queryInput()?.nativeElement.focus());

    if (this.detachOpenListeners) return;
    this.zone.runOutsideAngular(() => {
      const onDown = (e: MouseEvent) => {
        const target = e.target as Element | null;
        if (!target || this.host.contains(target) || target.closest('[data-page-finder-toggle]')) return;
        this.zone.run(() => this.finder.close());
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') this.zone.run(() => this.finder.close());
      };
      document.addEventListener('mousedown', onDown, true);
      document.addEventListener('keydown', onKey);
      this.detachOpenListeners = () => {
        document.removeEventListener('mousedown', onDown, true);
        document.removeEventListener('keydown', onKey);
        this.detachOpenListeners = undefined;
      };
    });
  }

  private onClosed(): void {
    this.detachOpenListeners?.();
    // Drop the element references so a closed card holds nothing from the page.
    this.items.set([]);
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => this.host.querySelector('.bc-pf-active')?.scrollIntoView({ block: 'nearest' }));
  }
}

/**
 * Lower is better; null when a typed word is missing. Every word must appear in the label, hint or
 * keywords; then the whole label, its start, the start of one of its words, anywhere.
 */
function matchScore(item: FinderItem, term: string, words: string[]): number | null {
  const label = item.label.toLowerCase();
  const text = [label, item.hint, item.keywords].filter(Boolean).join(' ').toLowerCase();
  if (!words.every(w => text.includes(w))) return null;
  if (label === term) return 0;
  if (label.startsWith(term)) return 1;
  if (label.split(/[\s/&-]+/).some(part => part.startsWith(words[0]))) return 2;
  return label.includes(words[0]) ? 3 : 4;
}
